/**
 * Admin Console error descriptions — operator-facing.
 *
 * This table is DISTINCT from `packages/public/types/src/error-messages.ts`:
 * - `error-messages.ts` holds end-user strings + i18n keys for the mobile/web
 *   client (customer-facing).
 * - `admin-error-messages.ts` holds short operator descriptions for the ops
 *   console toasts, log rows, and dashboards (internal-facing). Operators
 *   need to triage incidents quickly, so the text answers "what does this
 *   code mean and where do I look next?" rather than "how does the user
 *   recover?".
 *
 * Scope: Admin UI is en + zh only (consistent with `apps/admin/src/i18n/`).
 * Every code defined in `packages/public/types/src/errors.ts` has an entry
 * here so unknown-code fallbacks never leak to the ops console.
 *
 * Source: Round 5 W2E finding E-08 — operators currently see raw error codes
 * (`apps/admin/src/hooks/use-auth.ts` has ad-hoc strings only for
 * ACCOUNT_FROZEN / ADMIN_REQUIRED). Replaces that ad-hoc pattern with a
 * complete table.
 */

import { ERROR_CODES, type ErrorCode } from "@hasheeai/types";

type AdminLocale = "en" | "zh";

type AdminErrorEntry = {
  /** Short operator-friendly English summary (≤80 chars) */
  readonly en: string;
  /** Short operator-friendly Chinese summary (≤40 字) */
  readonly zh: string;
  /** Longer context: when it fires + where to look for root cause */
  readonly description: string;
};

/**
 * Complete operator description table.
 * Keyed by `ErrorCode` (derived from `ERROR_CODES`), so any new backend code
 * that lacks an entry here will fail TypeScript `Record<ErrorCode, …>`
 * exhaustiveness check at build time.
 */
const ADMIN_ERROR_MESSAGES: Record<ErrorCode, AdminErrorEntry> = {
  // ─── Auth / Account (ACC) ───
  INVALID_EMAIL: {
    en: "Client-side email format validation failed",
    zh: "客户端邮箱格式校验失败",
    description:
      "User submitted malformed email; backend rejects before rate-limit counter.",
  },
  INVALID_PASSWORD: {
    en: "Client-side password strength check failed",
    zh: "客户端密码强度不足",
    description: "Account password below ≥8 character bcrypt threshold.",
  },
  EMAIL_ALREADY_EXISTS: {
    en: "Email is already registered",
    zh: "邮箱已被注册",
    description:
      "Duplicate registration attempt. Check `users.email` uniqueness.",
  },
  IDENTITY_BUSY: {
    en: "Identity claim in flight — try again",
    zh: "身份正在被其他注册尝试占用,请稍后重试",
    description:
      "P1-B6 IdentityShardDO admission lock held by another in-flight " +
      "register attempt for same email/phone/username key. Caller should " +
      "retry after the DO TTL (~10s) or check `admission_locks` table " +
      "for the pending row (status='pending'). Race-loser response in " +
      "1000-concurrent-same-email register test.",
  },
  INVALID_CODE: {
    en: "Verification code is wrong",
    zh: "验证码错误",
    description:
      "Check `verification_codes` table; user may have typoed or used expired code.",
  },
  CODE_EXPIRED: {
    en: "Verification code has expired",
    zh: "验证码已过期",
    description:
      "Code TTL 15min exceeded. Tell user to re-request via /auth/send-code.",
  },
  CODE_RATE_LIMIT: {
    en: "Verification code send rate limited",
    zh: "验证码发送被限流",
    description:
      "sendCodeRateLimiter (1/email/60s). Check rate-limiter KV or wait window.",
  },
  ACCOUNT_LOCKED: {
    en: "Account locked after 5 failed logins (15min)",
    zh: "登录失败 5 次，账号锁定 15 分钟",
    description:
      "FuncSpec ACC-005b. Check `users.login_locked_until`. Resolves automatically.",
  },
  KEYRING_GENERATION_FAILED: {
    en: "Client failed to generate E2EE keyring",
    zh: "客户端 E2EE 密钥生成失败",
    description:
      "Client-side crypto API failure. Check mobile crash reports for context.",
  },
  KEY_BACKUP_UPLOAD_FAILED: {
    en: "Key backup upload failed",
    zh: "密钥备份上传失败",
    description:
      "POST /keys/backup rejected. Check `encrypted_key_backups` write path.",
  },
  USER_HAS_AGENTS: {
    en: "Cannot deactivate: user still owns agents",
    zh: "用户仍拥有 Agent，无法注销",
    description:
      "User must transfer or delete all agents before self-deactivation.",
  },
  ACCOUNT_DEACTIVATED: {
    en: "Account has been deactivated",
    zh: "账号已注销",
    description:
      "Check `users.deactivated_at`. Cannot be reversed without DB intervention.",
  },
  WRONG_PASSWORD: {
    en: "Account password is incorrect",
    zh: "账号密码错误",
    description: "bcrypt mismatch on login. Increments `failed_login_count`.",
  },
  ALREADY_USING: {
    en: "Relation / feature already in use by peer",
    zh: "对方已使用该关系/功能",
    description: "REL_005. Check `h2h_relations` or feature-specific state.",
  },
  ACCOUNT_DELETED: {
    en: "Account was deleted after grace period",
    zh: "账号已过期删除",
    description: "Post-deactivation purge. Check `users.deleted_at`.",
  },
  ACCOUNT_FROZEN: {
    en: "Account frozen by admin",
    zh: "账号已被管理员冻结",
    description:
      "Check `users.frozen_at`. Unfreeze via `POST /admin/users/:id/unfreeze`.",
  },
  EMAIL_DISPOSABLE_BLOCKED: {
    en: "Registration blocked disposable email",
    zh: "一次性邮箱注册被拦截",
    description:
      "Registration rejected by disposable-email middleware. Check the submitted email domain and `apps/api/src/lib/disposable-emails.ts` before changing the blocklist.",
  },

  // ─── Auth Flow (AUTH) ───
  UNAUTHORIZED: {
    en: "Missing or invalid credentials",
    zh: "未授权或凭证无效",
    description:
      "Generic auth failure. Check JWT header + AUTH_SESSIONS KV presence.",
  },
  TOKEN_EXPIRED: {
    en: "Access token expired",
    zh: "访问令牌已过期",
    description:
      "Access token TTL 15min. Client should auto-refresh via /auth/refresh.",
  },
  REFRESH_TOKEN_EXPIRED: {
    en: "Refresh token expired",
    zh: "刷新令牌已过期",
    description: "Refresh token TTL 30d. User must re-login.",
  },
  PRIVATE_KEY_DECRYPTION_FAILED: {
    en: "Client failed to unlock private key",
    zh: "客户端私钥解密失败",
    description:
      "Wrong protection password or corrupted Backup A. User must try another backup slot.",
  },
  WS_HANDSHAKE_TIMEOUT: {
    en: "WebSocket first-frame handshake timed out",
    zh: "WebSocket 首帧超时",
    description:
      "5s timeout on WS auth frame. Check client network or DO warm-up.",
  },
  TURNSTILE_FAILED: {
    en: "Cloudflare Turnstile verification failed",
    zh: "Cloudflare Turnstile 校验失败",
    description:
      "Check Turnstile secret + client widget. Dev uses test key 1x0000...AA.",
  },
  INVALID_CREDENTIALS: {
    en: "Email + password combination incorrect",
    zh: "邮箱或密码不正确",
    description:
      "Canonical login failure. Does NOT distinguish bad email vs bad password (by design).",
  },
  IDENTITY_PENDING_VERIFICATION: {
    en: "Auth pending — password change in flight or KV mirror stale",
    zh: "身份验证暂时不可用 — 改密中或 KV 镜像滞后",
    description:
      "P1-B8 R17-F1/R19-F1. AuthCredentialDO has pending change OR KV `auth:cred.kv_epoch` lags DO `current_epoch`. Client should retry after 1-3s. Lock window ≤90s (cron sweep finishes the prior 2PC).",
  },

  // ─── Passkey ───
  PASSKEY_REGISTRATION_FAILED: {
    en: "Passkey registration failed",
    zh: "Passkey 注册失败",
    description:
      "WebAuthn attestation invalid or conflict. Check `passkey_credentials` table.",
  },
  PASSKEY_AUTH_FAILED: {
    en: "Passkey authentication failed",
    zh: "Passkey 认证失败",
    description:
      "Assertion verification failed. Rate-limited by passkeyIpRateLimiter.",
  },
  PASSKEY_NOT_FOUND: {
    en: "Passkey credential not found",
    zh: "Passkey 凭证不存在",
    description: "Target credential_id does not exist for this user.",
  },
  PASSKEY_LIMIT_EXCEEDED: {
    en: "User has reached Passkey credential limit",
    zh: "Passkey 数量已达上限",
    description: "PASSKEY_001. FuncSpec limits per-user credential count.",
  },
  PASSKEY_CANNOT_DELETE_LAST: {
    en: "Cannot delete the last Passkey credential",
    zh: "不能删除最后一个 Passkey",
    description:
      "Safety guard — user would lose Passkey recovery path entirely.",
  },

  // ─── User / Profile ───
  USER_NOT_FOUND: {
    en: "User does not exist",
    zh: "用户不存在",
    description: "Check `users` table. May also indicate soft-deleted user.",
  },
  CUSTOM_ID_TAKEN: {
    en: "Custom ID (@handle) already taken",
    zh: "自定义 ID 已被占用",
    description: "Unique constraint on `users.custom_id`. Check target row.",
  },
  CUSTOM_ID_CHANGE_LIMIT: {
    en: "User has reached custom ID change limit",
    zh: "自定义 ID 修改次数超限",
    description:
      "FuncSpec allows finite number of renames. Check `users.custom_id_change_count`.",
  },
  INVALID_DISPLAY_NAME: {
    en: "Display name failed client-side validation",
    zh: "显示名客户端校验失败",
    description: "2-24 character limit. Server rejects before DB write.",
  },
  INVALID_BIO: {
    en: "Bio failed client-side validation",
    zh: "简介客户端校验失败",
    description: "0-200 character limit.",
  },
  INVALID_CUSTOM_ID: {
    en: "Custom ID format invalid",
    zh: "自定义 ID 格式不合法",
    description: "Must be 3-30 alphanumeric/underscore.",
  },
  SELF_ACTION_NOT_ALLOWED: {
    en: "User cannot perform this action on themselves",
    zh: "不允许对自己执行此操作",
    description: "Block/friend-request/device-verify self-targets.",
  },
  PROFILE_AVATAR_UPLOAD_FAILED: {
    en: "Avatar upload to R2 failed",
    zh: "头像上传到 R2 失败",
    description: "PROFILE_003. Check R2 binding + presigned URL validity.",
  },
  PROFILE_FIELD_INVALID: {
    en: "Profile field format invalid",
    zh: "用户资料字段格式不合法",
    description: "PROFILE_004. Generic field validation failure.",
  },

  // ─── Device ───
  DEVICE_NOT_FOUND: {
    en: "Device does not exist",
    zh: "设备不存在",
    description: "Check `user_devices` table. May have been deleted by user.",
  },
  DEVICE_LIMIT_EXCEEDED: {
    en: "User has too many authorized devices",
    zh: "设备数量已达上限",
    description:
      "FuncSpec DEV_001 cap. User must delete an existing device first.",
  },
  DEVICE_AUTH_TIMEOUT: {
    en: "Device authorization window expired (60s)",
    zh: "设备授权超时 (60 秒)",
    description: "Old device did not approve in time. User must retry.",
  },
  DEVICE_REMOVAL_FAILED: {
    en: "Device removal failed",
    zh: "设备移除失败",
    description: "DEV_002. Check KV session cleanup + DB transaction.",
  },
  DEVICE_AUTH_REJECTED: {
    en: "Device authorization rejected by user",
    zh: "设备授权被用户拒绝",
    description: "User pressed /auth/devices/:id/reject.",
  },
  DEVICE_ID_CONFLICT: {
    en: "Device ID owned by another user",
    zh: "设备 ID 已归属其他用户",
    description: "DEV_004. Unique constraint on (device_id) across users.",
  },
  DEVICE_FIELDS_REQUIRED: {
    en: "Device registration missing required fields",
    zh: "设备注册缺少必填字段",
    description: "DEV_005. device_id + public_key must be present.",
  },
  DEVICE_ID_MISMATCH: {
    en: "Device ID mismatch between request and JWT",
    zh: "设备 ID 不匹配",
    description: "DEV_006. Suspicious — possible session hijack attempt.",
  },
  DEVICE_KEY_MISMATCH: {
    en: "Device public key does not match stored key",
    zh: "设备公钥不匹配",
    description:
      "DEV_007. Possible key compromise. Check `user_devices.public_key`.",
  },
  PENDING_DEVICE_LIMIT: {
    en: "Too many pending (unauthorized) devices",
    zh: "待授权设备过多",
    description: "DEV_008. User must approve or reject pending devices first.",
  },
  DEVICE_NOT_AUTHORIZED: {
    en: "Device is in pending state, not authorized",
    zh: "设备未授权",
    description: "DEV_009. Device needs approval from an existing device.",
  },
  DEVICE_ID_JWT_MISMATCH: {
    en: "JWT device_id does not match header device_id",
    zh: "JWT 与请求头 device_id 不一致",
    description: "DEV_010. Possible session spoofing attempt.",
  },
  KEY_PROOF_INVALID: {
    en: "Device key proof signature invalid",
    zh: "设备密钥所有权证明无效",
    description: "DEV_011. Client failed to sign challenge with private key.",
  },
  BACKUP_NOT_CONFIGURED: {
    en: "User has no key backup configured",
    zh: "用户未配置密钥备份",
    description:
      "DEV_012. User cannot authorize new device without Backup A/B.",
  },
  CANNOT_DELETE_CURRENT_DEVICE: {
    en: "Cannot delete the device currently in use",
    zh: "不能删除当前使用的设备",
    description: "DEV_013. User must logout or switch devices first.",
  },
  REVOKE_TOKEN_INVALID: {
    en: "Device revoke token invalid or expired",
    zh: "设备撤销令牌无效或已过期",
    description: "DEV_014. Magic-link token from 'not me' email.",
  },

  // ─── Admin / Governance ───
  ADMIN_REQUIRED: {
    en: "Admin privileges required",
    zh: "需要管理员权限",
    description: "User not in `ADMIN_USER_IDS` env whitelist.",
  },
  DLQ_ENTRY_NOT_FOUND: {
    en: "DLQ entry not found in R2 archive",
    zh: "DLQ 归档中未找到该条目",
    description:
      "Admin DLQ replay: requested ulid not present at expected R2 path; entry may have expired or been replayed already.",
  },
  DLQ_INTERNAL_ERROR: {
    en: "DLQ replay internal error",
    zh: "DLQ 重放内部错误",
    description:
      "Admin DLQ replay: R2 fetch / queue enqueue failed unexpectedly; check Worker logs.",
  },
  DEVICE_LOGIN_LOG_WRITE_FAILED: {
    en: "Failed to write device login audit log",
    zh: "设备登录日志写入失败",
    description:
      "Internal log code: device_login_log INSERT failed; best-effort path, does not block login.",
  },
  JOIN_REQUEST_NOT_FOUND: {
    en: "Join request does not exist",
    zh: "加入请求不存在",
    description: "Check `group_join_requests` table.",
  },
  KEY_BACKUP_ALREADY_EXISTS: {
    en: "Key backup already uploaded for this device",
    zh: "该设备的密钥备份已存在",
    description: "Use PUT /keys/backup to update, not POST.",
  },
  PASSKEY_ALREADY_REGISTERED: {
    en: "This passkey is already registered",
    zh: "该 Passkey 已注册",
    description: "Credential ID conflict.",
  },
  FILE_TYPE_FORBIDDEN: {
    en: "File type not in whitelist",
    zh: "文件类型不在白名单",
    description:
      "Check MIME type against `packages/internal/config/file-types.ts`.",
  },
  FILE_NOT_UPLOADED: {
    en: "File was never uploaded to R2",
    zh: "文件未成功上传到 R2",
    description: "Confirm endpoint called before upload completed.",
  },
  FILE_HASH_MISMATCH: {
    en: "File integrity hash mismatch",
    zh: "文件哈希校验失败",
    description: "R2 object's SHA-256 does not match client-declared hash.",
  },
  FILE_SIZE_MISMATCH: {
    en: "File size mismatch",
    zh: "文件大小不匹配",
    description: "R2 object size differs from presigned-upload declared size.",
  },

  // ─── Key Restore (RESTORE) ───
  RESTORE_TOKEN_INVALID: {
    en: "Key restore token invalid",
    zh: "密钥恢复令牌无效",
    description: "RESTORE_001. Check KV for restore token or expiry.",
  },
  BIOMETRIC_VERIFICATION_FAILED: {
    en: "Biometric verification failed",
    zh: "生物识别验证失败",
    description:
      "RESTORE_002. Client-side Touch ID/Face ID failure. User should fall back to protection password.",
  },
  RESTORE_PASSWORD_INCORRECT: {
    en: "Protection password incorrect",
    zh: "保护密码错误",
    description:
      "RESTORE_003. User typo on protection password during key restore.",
  },
  RESTORE_NETWORK_TIMEOUT: {
    en: "Key restore network timeout",
    zh: "密钥恢复网络超时",
    description: "RESTORE_004. Client failed to fetch backup in time.",
  },

  // ─── Relation ───
  ALREADY_FRIENDS: {
    en: "Users are already friends",
    zh: "已是好友",
    description: "Idempotent friend request — safe to ignore.",
  },
  REQUEST_COOLDOWN_ACTIVE: {
    en: "Friend request cooldown active",
    zh: "好友申请冷却中",
    description: "REL_002. 24h cooldown after rejection.",
  },
  NOT_FRIENDS: {
    en: "Not friends — action requires friendship",
    zh: "非好友，无法操作",
    description: "Privacy setting blocks non-friends.",
  },
  FRIEND_REQUEST_EXISTS: {
    en: "A friend request is already pending",
    zh: "已有好友申请待处理",
    description: "Check `requests` table for existing pending row.",
  },
  RELATION_NOT_FOUND: {
    en: "Relation does not exist",
    zh: "关系不存在",
    description: "Check `h2h_relations` or `h2a_relations` table.",
  },
  RELATION_TERMINATED: {
    en: "Relation is terminated (unfriended/blocked)",
    zh: "关系已终止",
    description: "Check relation.terminated_at or block state.",
  },
  RELATION_SUSPENDED: {
    en: "Relation is suspended",
    zh: "关系已暂停",
    description: "Mid-state before termination. Check suspension reason.",
  },
  BLOCKED_BY_USER: {
    en: "Action target has blocked this user",
    zh: "被对方拉黑",
    description:
      "Silent rejection — client sees no distinction from other NOT_FRIENDS errors in most flows.",
  },
  BLOCKED_USER: {
    en: "Cannot interact with a blocked user",
    zh: "不能与已拉黑用户互动",
    description: "Current user has blocked target.",
  },
  H2A_APPROVAL_REJECTED: {
    en: "H2A (human-to-agent) connection rejected",
    zh: "Agent 连接请求被拒绝",
    description: "REL_004. User rejected the agent install/connection.",
  },

  // ─── Block ───
  BLOCK_NOT_FOUND: {
    en: "Block record does not exist",
    zh: "拉黑记录不存在",
    description: "Check `user_blocks` table.",
  },
  ALREADY_BLOCKED: {
    en: "User is already blocked",
    zh: "用户已被拉黑",
    description: "Idempotent block request.",
  },
  CANNOT_BLOCK_SELF: {
    en: "Cannot block yourself",
    zh: "不能拉黑自己",
    description: "Self-action guard.",
  },

  // ─── Request ───
  REQUEST_NOT_FOUND: {
    en: "Request does not exist",
    zh: "请求不存在",
    description: "Check `requests` table. May have been already processed.",
  },
  REQUEST_ALREADY_PROCESSED: {
    en: "Request has already been processed",
    zh: "请求已处理",
    description: "Check request.status — already accepted/rejected.",
  },
  REQUEST_EXPIRED: {
    en: "Request expired (TTL reached)",
    zh: "请求已过期",
    description: "FuncSpec specifies request TTL per type.",
  },
  CANNOT_REQUEST_SELF: {
    en: "Cannot send request to yourself",
    zh: "不能向自己发送请求",
    description: "Self-action guard.",
  },

  // ─── Conversation ───
  CONVERSATION_NOT_FOUND: {
    en: "Conversation does not exist",
    zh: "会话不存在",
    description: "Check `conversations` table. May be H2H/H2A/Group.",
  },
  NOT_CONVERSATION_MEMBER: {
    en: "User is not a member of this conversation",
    zh: "非会话成员",
    description: "Check `conversation_members` or group membership.",
  },
  CONVERSATION_FROZEN: {
    en: "Conversation frozen (read-only)",
    zh: "会话已冻结",
    description:
      "Admin froze the conversation. Check `conversations.frozen_at`.",
  },
  CONVERSATION_TERMINATED: {
    en: "Conversation has ended",
    zh: "会话已终止",
    description: "Relation terminated → conversation marked ended.",
  },
  CONVERSATION_CACHE_EMPTY: {
    en: "Client-side conversation cache empty",
    zh: "客户端会话缓存为空",
    description: "CHAT_001. Client-side only — not a backend issue.",
  },
  METADATA_SYNC_FAILED: {
    en: "Client metadata sync failed",
    zh: "客户端元数据同步失败",
    description: "CHAT_002. Client-side only.",
  },
  H2A_NOT_ESTABLISHED: {
    en: "H2A relationship not established",
    zh: "Agent 关系尚未建立",
    description: "CHAT_004. User must first send an agent invitation.",
  },

  // ─── Message ───
  MESSAGE_NOT_FOUND: {
    en: "Message does not exist",
    zh: "消息不存在",
    description: "Check DO storage (messages live in DO SQLite, not Neon).",
  },
  RECALL_WINDOW_EXPIRED: {
    en: "Message recall window expired",
    zh: "消息撤回窗口已过期",
    description:
      "Recall allowed within FuncSpec MSG-003 window (default 2min).",
  },
  MESSAGE_ALREADY_RECALLED: {
    en: "Message already recalled",
    zh: "消息已撤回",
    description: "Idempotent recall.",
  },
  NOT_MESSAGE_SENDER: {
    en: "Only the sender can perform this action",
    zh: "只有发送者可以操作",
    description: "Recall/edit must be initiated by original sender.",
  },
  MESSAGE_NOT_FORWARDABLE: {
    en: "Message is not forwardable",
    zh: "消息不可转发",
    description: "MSG_004. Message had forwardable=false.",
  },
  TARGET_CONVERSATION_NO_PERMISSION: {
    en: "No permission in target conversation",
    zh: "在目标会话无权限",
    description: "MSG_005. Forward target conversation blocks sender.",
  },
  INVALID_CONTENT_TYPE: {
    en: "Unknown message content_type",
    zh: "未知的消息类型",
    description: "Not in the 10 canonical content types.",
  },
  PAYLOAD_TOO_LARGE: {
    en: "Message payload exceeds size limit",
    zh: "消息体超出大小限制",
    description: "See Message Spec size caps per payload type.",
  },
  EMPTY_CONTENT: {
    en: "Empty message content rejected",
    zh: "消息内容为空",
    description: "Client-side guard should have caught this.",
  },
  INVALID_MENTIONS: {
    en: "Mention list invalid",
    zh: "提及列表无效",
    description: "Mention targets not in conversation.",
  },
  MENTION_ALL_NOT_ALLOWED: {
    en: "@all mention not allowed",
    zh: "不允许 @所有人",
    description: "Group permission restricts @all to admins.",
  },
  IDEMPOTENCY_KEY_CONFLICT: {
    en: "Idempotency key already used",
    zh: "幂等键冲突",
    description: "Duplicate message send — client should backoff.",
  },
  FORWARD_TARGET_LIMIT: {
    en: "Too many forward targets (max 10)",
    zh: "转发目标超出上限",
    description: "Single forward call supports ≤10 target conversations.",
  },
  ARTIFACT_NOT_FORWARDABLE: {
    en: "Artifact is not forwardable",
    zh: "Artifact 不可转发",
    description: "Artifact forwardable=false (ACTION-type artifacts).",
  },
  HUMAN_CANNOT_SEND_ARTIFACT: {
    en: "Humans cannot send artifacts (agent-only)",
    zh: "用户不能发送 Artifact（仅 Agent 可发）",
    description: "A2H v0.3 contract — artifacts are agent output.",
  },
  MESSAGE_ENCRYPTION_FAILED: {
    en: "Client-side message encryption failed",
    zh: "客户端消息加密失败",
    description: "MSG_024. Client crypto failure — check crash report.",
  },
  MESSAGE_ID_REQUIRED: {
    en: "message_id missing on a write that requires one",
    zh: "缺少 message_id（recall / 反应 / 已读回执需要）",
    description:
      "ConversationDO 400 — recall, reaction, read-receipt target, or edit arrived without a valid message_id.",
  },
  QUOTE_CHAIN_NOT_ALLOWED: {
    en: "Quoted message itself quotes another (1-level cap)",
    zh: "引用链超过 1 层（V1 限制）",
    description:
      "ConversationDO 400 — FuncSpec §20 MSG-005 quote-chain rule. V1 allows quote depth of 1 only.",
  },

  // ─── Reaction ───
  REACTION_NOT_WHITELISTED: {
    en: "Reaction emoji not in whitelist",
    zh: "Reaction 不在白名单内",
    description: "REACT_001. Only the whitelisted emoji set is allowed.",
  },

  // ─── Read receipt ───
  READ_RECEIPT_REPORT_FAILED: {
    en: "Client failed to report read receipt",
    zh: "已读回执上报失败",
    description: "READ_001. Client-side WS frame send failed.",
  },

  // ─── Verification Code ───
  CODE_SEND_FAILED: {
    en: "Verification code send failed",
    zh: "验证码发送失败",
    description: "CODE_001. Check Resend API status + logs.",
  },
  CODE_SCENE_INVALID: {
    en: "Verification code scene is invalid",
    zh: "验证码场景无效",
    description: "CODE_002. Scene must be register/login/reset/change-email.",
  },
  CODE_ALREADY_USED: {
    en: "Verification code already used",
    zh: "验证码已被使用",
    description: "CODE_003. One-time codes cannot be re-used.",
  },
  CODE_MAX_ATTEMPTS: {
    en: "Verification code exceeded max attempts",
    zh: "验证码尝试次数超限",
    description: "CODE_004. Code locked after too many wrong entries.",
  },
  CODE_EMAIL_NOT_REGISTERED: {
    en: "Reset-password email not registered",
    zh: "重置密码：邮箱未注册",
    description:
      "CODE_005. Intentionally distinct from silent-fail policy — legal/product decision.",
  },

  // ─── Agent ───
  AGENT_NOT_FOUND: {
    en: "Agent does not exist",
    zh: "Agent 不存在",
    description: "Check `agent_templates` table.",
  },
  AGENT_DELETED: {
    en: "Agent has been deleted",
    zh: "Agent 已被删除",
    description: "Check `agent_templates.deleted_at`.",
  },
  NOT_AGENT_CREATOR: {
    en: "Only the Agent creator can perform this action",
    zh: "只有 Agent 创建者可操作",
    description: "Admin/governance endpoints check creator_id.",
  },
  AGENT_CUSTOM_ID_TAKEN: {
    en: "Agent custom ID already taken",
    zh: "Agent 自定义 ID 已被占用",
    description: "Unique constraint on `agent_templates.custom_id`.",
  },
  INVALID_AGENT_TOKEN: {
    en: "Agent token invalid or revoked",
    zh: "Agent 令牌无效或已撤销",
    description: "Check `agent_tokens` table + KV blacklist.",
  },
  AGENT_TOKEN_EXPIRED: {
    en: "Agent token expired",
    zh: "Agent 令牌已过期",
    description: "Token TTL exceeded. Creator must regenerate.",
  },
  AGENT_ALREADY_CONNECTED: {
    en: "Agent already has an active WS connection",
    zh: "Agent 已有活跃 WebSocket 连接",
    description: "Single-WS constraint per agent_connection_do instance.",
  },
  INVALID_WEBHOOK_URL: {
    en: "Webhook URL format invalid",
    zh: "Webhook URL 格式无效",
    description: "Must be HTTPS + valid URL.",
  },
  INVALID_CONNECTION_MODE: {
    en: "Agent connection mode invalid",
    zh: "Agent 连接模式无效",
    description: "Must be 'ws' or 'webhook'.",
  },
  AGENT_LIMIT_EXCEEDED: {
    en: "User reached max agent creation quota",
    zh: "用户 Agent 创建配额已达上限",
    description: "AGT_008. Check `agent_templates.creator_id` count.",
  },
  AGENT_RESTRICTED: {
    en: "Agent is in restricted state",
    zh: "Agent 处于受限状态",
    description: "Admin restricted via `POST /admin/agents/:id/restrict`.",
  },
  AGENT_SUSPENDED: {
    en: "Agent is suspended",
    zh: "Agent 已暂停",
    description: "Legacy alias for restricted in some flows.",
  },
  AGENT_BANNED: {
    en: "Agent is banned",
    zh: "Agent 已被封禁",
    description:
      "Admin banned via `POST /admin/agents/:id/ban`. Not reversible.",
  },
  AGENT_KEY_REGISTER_CONFLICT: {
    en: "Agent key registration write conflicted",
    zh: "Agent 密钥注册写入冲突",
    description:
      "Guarded `POST /agents/:id/keys/register` update returned no row. " +
      "Check concurrent key-register attempts and `agent_templates` row state.",
  },
  AGENT_SIGNING_KEY_ROTATION_UNSUPPORTED: {
    en: "Agent signing key replacement was blocked",
    zh: "Agent 签名密钥替换被阻断",
    description:
      "Existing Ed25519 signing key differed from the supplied key. This is " +
      "blocked to preserve historical H2A message signer binding.",
  },
  CREATOR_HAS_BANNED_AGENT: {
    en: "Agent creator has a previously-banned agent",
    zh: "创建者名下有被封禁的 Agent",
    description:
      "AGT_013. New agent creation blocked until banned agent is resolved.",
  },
  AGENT_NOT_WEBHOOK_MODE: {
    en: "Agent is not configured for webhook delivery",
    zh: "Agent 不是 Webhook 模式",
    description:
      "AGT_016. Webhook-secret rotation only applies to webhook-mode agents; check `agent_templates.connection_mode`.",
  },
  WEBHOOK_TEST_FAILED: {
    en: "Webhook test probe did not return 2xx within the deadline",
    zh: "Webhook 测试探测未在期限内返回 2xx",
    description:
      "CD-005. Owner-initiated `POST /agents/:id/webhook/test` failed. Inspect `details.response_code`, `details.response_ms`, and `details.reason` (`timeout`, `network_error`, `non_2xx_response`). Does NOT change `agents.unreachable_since`.",
  },
  COMMAND_LIST_INVALID: {
    en: "Agent auth frame command_list failed schema validation",
    zh: "Agent 认证帧 command_list 校验失败",
    description:
      "AgentConnectionDO 4400 — handshake closed. Check agent's WS auth payload shape.",
  },
  NO_AUTHENTICATED_SOCKET: {
    en: "WS write/probe arrived before auth handshake completed",
    zh: "WS 帧在认证握手完成前到达",
    description:
      "AgentConnectionDO 401 — frame received on a socket that has not authenticated. Likely client bug or race.",
  },
  UNKNOWN_FRAME: {
    en: "WS frame type not in the accepted set for this mode",
    zh: "WS 帧类型不在当前模式允许集合内",
    description:
      "AgentConnectionDO / HumanSessionDO 400 — client sent an unrecognized frame type. Do not retry the same payload.",
  },

  // ─── Invite Code ───
  INVITE_CODE_NOT_FOUND: {
    en: "Invite code not found",
    zh: "邀请码不存在",
    description: "Check `invite_codes` table.",
  },
  INVITE_CODE_EXPIRED: {
    en: "Invite code expired",
    zh: "邀请码已过期",
    description: "Check `invite_codes.expires_at`.",
  },
  INVITE_CODE_FULL: {
    en: "Invite code usage limit reached",
    zh: "邀请码使用次数已满",
    description: "Check `invite_codes.used_count` vs `max_uses`.",
  },
  INVITE_CODE_INACTIVE: {
    en: "Invite code is inactive",
    zh: "邀请码已失效",
    description: "Admin disabled the code via `PATCH /admin/invite-codes/:id`.",
  },
  USER_INVITE_CODE_INVALID: {
    en: "User-generated invite code invalid",
    zh: "用户生成的邀请码无效",
    description: "INV_005. Separate from admin codes.",
  },
  USER_INVITE_CODE_EXPIRED: {
    en: "User-generated invite code expired",
    zh: "用户生成的邀请码已过期",
    description: "INV_006.",
  },
  USER_INVITE_CODE_CLAIMED: {
    en: "User-generated invite code already claimed",
    zh: "用户生成的邀请码已被使用",
    description: "INV_007. Single-use user invite codes.",
  },
  USER_INVITE_QUOTA_EXCEEDED: {
    en: "User reached invite code generation quota",
    zh: "用户邀请码生成配额已达上限",
    description: "INV_008. Per-user quota prevents spam.",
  },

  // ─── Registration Application ───
  APPLICATION_DUPLICATE: {
    en: "Duplicate registration application",
    zh: "重复的注册申请",
    description: "APP_001. One open application per email.",
  },
  APPLICATION_NOT_FOUND: {
    en: "Registration application not found",
    zh: "注册申请不存在",
    description: "APP_002. Check `registration_applications` table.",
  },

  // ─── Command ───
  COMMAND_LIMIT_EXCEEDED: {
    en: "Agent slash command count limit exceeded",
    zh: "Agent 斜杠命令数量超限",
    description: "CMD_001. FuncSpec cap per agent.",
  },
  CMD_002: {
    en: "Agent has been deleted for this command surface",
    zh: "Agent 已删除，无法读取或更新命令",
    description:
      "CMD_002. Command registry endpoints return this 404 for soft-deleted agents.",
  },
  COMMAND_NAME_INVALID: {
    en: "Slash command name invalid",
    zh: "斜杠命令名称无效",
    description: "Slash command name must match /^[a-z][a-z0-9_-]*$/.",
  },

  // ─── Group ───
  GROUP_NOT_FOUND: {
    en: "Group does not exist",
    zh: "群不存在",
    description: "Check `groups` table.",
  },
  GROUP_CLOSED: {
    en: "Group is closed",
    zh: "群已关闭",
    description: "GRP_002. Group owner dissolved the group.",
  },
  GROUP_NO_EDIT_PERMISSION: {
    en: "No permission to edit group info",
    zh: "无权编辑群信息",
    description: "GRP_003. Check group_members.role.",
  },
  GROUP_AVATAR_UPLOAD_FAILED: {
    en: "Group avatar upload to R2 failed",
    zh: "群头像上传到 R2 失败",
    description: "GRP_004.",
  },
  GROUP_FIELD_INVALID: {
    en: "Group field format invalid",
    zh: "群字段格式无效",
    description: "GRP_005.",
  },
  GROUP_DELETED: {
    en: "Group has been deleted",
    zh: "群已被删除",
    description: "GRP_006. Soft-deleted via `groups.deleted_at`.",
  },
  GROUP_NO_REMOVE_PERMISSION: {
    en: "No permission to remove group member",
    zh: "无权移除群成员",
    description: "GRP_007.",
  },
  GROUP_TARGET_HIGHER_PRIVILEGE: {
    en: "Target member has higher privilege",
    zh: "目标成员权限高于当前用户",
    description: "GRP_008. Privilege hierarchy guard.",
  },
  NOT_GROUP_MEMBER: {
    en: "User is not a member of the group",
    zh: "非群成员",
    description: "GRP_009. Check `group_members`.",
  },
  NOT_GROUP_ADMIN: {
    en: "User is not a group admin",
    zh: "非群管理员",
    description: "GRP_010.",
  },
  GROUP_SETTINGS_SAVE_FAILED: {
    en: "Failed to save group settings",
    zh: "保存群设置失败",
    description: "GRP_011. DB write failure.",
  },
  NOT_GROUP_OWNER: {
    en: "User is not the group owner",
    zh: "非群主",
    description: "GRP_012. Owner-only operation.",
  },
  GROUP_TARGET_NOT_HUMAN_MEMBER: {
    en: "Group action target must be a human member",
    zh: "目标必须是人类群成员",
    description: "GRP_013. Cannot target agents for certain operations.",
  },
  GROUP_NO_ANNOUNCEMENT_PERMISSION: {
    en: "No permission to post group announcement",
    zh: "无权发布群公告",
    description: "GRP_014.",
  },
  GROUP_ANNOUNCEMENT_TOO_LONG: {
    en: "Group announcement exceeds length limit",
    zh: "群公告超出长度限制",
    description: "GRP_015.",
  },
  GROUP_NO_MUTE_PERMISSION: {
    en: "No permission to mute group member",
    zh: "无权禁言群成员",
    description: "GRP_016.",
  },
  GROUP_TARGET_IS_OWNER_OR_PEER: {
    en: "Cannot mute owner or same-level admin",
    zh: "不能禁言群主或同级管理员",
    description: "GRP_017.",
  },
  GROUP_NO_JOIN_APPROVAL_PERMISSION: {
    en: "No permission to change join-approval setting",
    zh: "无权修改入群审批设置",
    description: "GRP_018.",
  },
  GROUP_NO_INVITE_PERMISSION_SETTING: {
    en: "No permission to change invite setting",
    zh: "无权修改邀请设置",
    description: "GRP_019.",
  },
  GROUP_FULL: {
    en: "Group member limit reached",
    zh: "群成员已达上限",
    description: "Check `groups.max_members`.",
  },
  ALREADY_GROUP_MEMBER: {
    en: "Already a group member",
    zh: "已是群成员",
    description: "Idempotent join.",
  },
  CANNOT_REMOVE_OWNER: {
    en: "Cannot remove the group owner",
    zh: "不能移除群主",
    description: "Owner must transfer ownership first.",
  },
  CANNOT_LEAVE_AS_OWNER: {
    en: "Owner cannot leave without transferring ownership",
    zh: "群主必须先转让群后才能退出",
    description: "Guard against orphan groups.",
  },
  INVALID_GROUP_NAME: {
    en: "Group name format invalid",
    zh: "群名称格式无效",
    description: "2-40 character limit.",
  },
  LAST_ADMIN: {
    en: "Cannot demote the last admin",
    zh: "不能降级最后一个管理员",
    description: "Guard against orphan admin set.",
  },
  MEMBER_MUTED: {
    en: "Member is muted — cannot send messages",
    zh: "成员已被禁言",
    description: "Check `group_members.muted_until`.",
  },
  TRANSFER_TO_SELF: {
    en: "Cannot transfer ownership to self",
    zh: "不能将群转让给自己",
    description: "Self-action guard.",
  },
  TRANSFER_TARGET_NOT_MEMBER: {
    en: "Transfer target is not a group member",
    zh: "转让目标非群成员",
    description: "Target must be existing admin/member.",
  },
  JOIN_APPROVAL_REQUIRED: {
    en: "Group requires admin approval to join",
    zh: "入群需要管理员审批",
    description: "Join creates pending request, not direct membership.",
  },
  INVITE_PERMISSION_DENIED: {
    en: "No permission to invite to this group",
    zh: "无权邀请进群",
    description: "Group setting restricts invites to admins.",
  },
  PINNED_MESSAGE_LIMIT: {
    en: "Pinned message count limit reached",
    zh: "置顶消息数量已达上限",
    description: "FuncSpec per-group pin cap.",
  },
  MESSAGE_ALREADY_PINNED: {
    en: "Message already pinned",
    zh: "消息已置顶",
    description: "Idempotent pin.",
  },
  GROUP_DEK_UNAVAILABLE: {
    en: "Group DEK not available yet",
    zh: "群加密密钥尚未就绪",
    description:
      "Wave 7 C. Group exists and caller is a member, but no DEK row yet OR no wrap entry for this caller. Admin needs to run POST /keys/groups/:id/rotate or the member-wrap endpoint.",
  },

  // ─── Artifact ───
  ARTIFACT_NOT_FOUND: {
    en: "Artifact does not exist",
    zh: "Artifact 不存在",
    description: "Check `artifact_items` table.",
  },
  ARTIFACT_EXPIRED: {
    en: "Artifact has expired (TTL)",
    zh: "Artifact 已过期",
    description: "Check `artifact_items.expires_at`.",
  },
  INVALID_REVISION: {
    en: "Artifact revision number invalid",
    zh: "Artifact 版本号无效",
    description: "Must strictly increase. Possible client race.",
  },
  ARTIFACT_STATUS_INVALID: {
    en: "Artifact state machine transition invalid",
    zh: "Artifact 状态机转换无效",
    description: "Cannot transition from current status.",
  },
  ARTIFACT_HEADER_INVALID: {
    en: "Artifact message header invalid",
    zh: "Artifact 消息头无效",
    description: "ART_001. A2H v0.3 header validation failed.",
  },
  ARTIFACT_TRUST_LEVEL_INSUFFICIENT: {
    en: "Artifact trust level insufficient",
    zh: "Artifact 信任等级不足",
    description: "ART_002. Feature requires higher trust level.",
  },
  ARTIFACT_STRUCTURE_INVALID: {
    en: "Artifact structure validation failed",
    zh: "Artifact 结构校验失败",
    description: "ART_004. Malformed A2H payload.",
  },
  ARTIFACT_CHECKPOINT_FAILED: {
    en: "Artifact checkpoint persistence failed",
    zh: "Artifact 检查点持久化失败",
    description: "ART_005. DO storage write failure.",
  },
  ARTIFACT_PHISHING_BLOCKED: {
    en: "Artifact blocked by anti-phishing filter",
    zh: "Artifact 被反钓鱼规则拦截",
    description: "ART_006. Suspicious URL or content pattern detected.",
  },
  ARTIFACT_AEAD_FAILED: {
    en: "Artifact AEAD encryption failed",
    zh: "Artifact AEAD 加密失败",
    description: "ART_007. Client-side crypto error.",
  },

  // ─── Task ───
  TASK_PARENT_NOT_FOUND: {
    en: "Task parent not found",
    zh: "任务父节点不存在",
    description: "TASK_001. Task pulse references missing parent.",
  },
  TASK_SIGNAL_INVALID: {
    en: "Task signal value invalid",
    zh: "任务信号无效",
    description: "TASK_002. Signal must match enum.",
  },

  // ─── Stream ───
  STREAM_ALREADY_ACTIVE: {
    en: "A stream is already active",
    zh: "已有活跃的流式传输",
    description: "Per-conversation stream is single-slot.",
  },
  STREAM_NOT_FOUND: {
    en: "Stream does not exist",
    zh: "流不存在",
    description: "Check DO stream state.",
  },
  STREAM_LIMIT_EXCEEDED: {
    en: "Stream count limit exceeded",
    zh: "流数量已达上限",
    description: "Global per-agent stream cap.",
  },
  STREAM_ORPHAN_DETECTED: {
    en: "Orphan stream detected and cleaned up",
    zh: "检测到孤儿流并清理",
    description: "Stream without matching WS connection — auto-cleanup.",
  },
  STREAM_STALE_TURN: {
    en: "Stale turn/stream frame rejected",
    zh: "过期轮次流帧被拒绝",
    description:
      "Agent UX billing freeze. Frame `(turn_id, stream_id)` did not match the active turn binding; inspect ConversationDO stream session state.",
  },
  STREAM_LARGE_GROUP_DISABLED: {
    en: "Streaming disabled in large groups",
    zh: "大群禁用流式传输",
    description: "STREAM_004. Performance guard.",
  },

  // ─── File ───
  FILE_TOO_LARGE: {
    en: "File exceeds size limit",
    zh: "文件超出大小限制",
    description: "Check per-type caps in validators.",
  },
  INVALID_FILE_TYPE: {
    en: "File MIME type not allowed",
    zh: "文件类型不被允许",
    description: "Check whitelist.",
  },
  UPLOAD_NOT_FOUND: {
    en: "Upload session not found",
    zh: "上传会话不存在",
    description: "Client must call /files/presign first.",
  },
  UPLOAD_EXPIRED: {
    en: "Upload session expired",
    zh: "上传会话已过期",
    description: "Presigned URL TTL reached.",
  },
  SHA256_MISMATCH: {
    en: "SHA256 hash mismatch",
    zh: "SHA256 哈希不匹配",
    description: "Integrity check failed.",
  },
  NOT_CONVERSATION_MEMBER_UPLOAD: {
    en: "Non-member cannot upload to conversation",
    zh: "非会话成员不能上传",
    description: "Permission guard.",
  },
  FILE_EXPIRED: {
    en: "File TTL expired",
    zh: "文件已过期",
    description: "FILE_006. R2 object deleted by retention policy.",
  },
  FILE_DECRYPTION_FAILED: {
    en: "Client file decryption failed",
    zh: "客户端文件解密失败",
    description: "FILE_007. Wrong key or corrupted ciphertext.",
  },
  FILE_DOWNLOAD_FAILED: {
    en: "File download failed",
    zh: "文件下载失败",
    description: "FILE_008. Check R2 availability.",
  },

  // ─── Typing ───
  TYPING_RATE_LIMITED: {
    en: "Typing indicator rate limited",
    zh: "正在输入指示器被限流",
    description: "TYPING_001. Per-user throttle.",
  },

  // ─── Knowledge ───
  KNOWLEDGE_ITEM_NOT_FOUND: {
    en: "Knowledge item not found",
    zh: "知识库条目不存在",
    description: "Check `knowledge_items` table.",
  },
  KNOWLEDGE_LIMIT_EXCEEDED: {
    en: "Knowledge base item count limit exceeded",
    zh: "知识库条目数量已达上限",
    description: "FuncSpec per-user cap.",
  },
  KNOWLEDGE_UPLOAD_FAILED: {
    en: "Knowledge base upload failed",
    zh: "知识库上传失败",
    description: "KB_001. R2/DB write failure.",
  },
  KNOWLEDGE_ENCRYPTION_FAILED: {
    en: "Knowledge encryption failed",
    zh: "知识库加密失败",
    description: "KB_002. Client-side crypto error.",
  },
  KNOWLEDGE_REFERENCE_DELETED: {
    en: "Knowledge reference target was deleted",
    zh: "知识库引用目标已删除",
    description: "KR_001. Dangling reference.",
  },
  KNOWLEDGE_REFERENCES_EXCEEDED: {
    en: "Knowledge reference count exceeded (max 3)",
    zh: "知识库引用数超限 (最多 3 个)",
    description: "KR_002.",
  },

  // ─── Summary ───
  SUMMARY_NOT_FOUND: {
    en: "Summary not found",
    zh: "摘要不存在",
    description: "SUM_001. Check `conversation_summary_threads`.",
  },
  SUMMARY_JOB_FAILED: {
    en: "Summary job failed",
    zh: "摘要任务失败",
    description:
      "SUM_002. Check job error detail. Note: post-Wave 1B-C summaries run in Hashee Agent, not backend.",
  },
  SUMMARY_JOB_NOT_FOUND: {
    en: "Summary job not found",
    zh: "摘要任务不存在",
    description: "SUM_003. Check `summary_jobs` table.",
  },
  SUMMARY_AI_UNAVAILABLE: {
    en: "Summary AI service unavailable",
    zh: "摘要 AI 服务不可用",
    description: "SUM_004. Workers AI binding or remote service down.",
  },
  SUMMARY_INPUT_TOO_LARGE: {
    en: "Summary input exceeds token limit",
    zh: "摘要输入超过 token 上限",
    description: "SUM_005. LLM context window cap.",
  },

  // ─── Delivery ───
  DELIVERY_ITEM_NOT_FOUND: {
    en: "Delivery item not found",
    zh: "交付物不存在",
    description: "DLV_001. Check `delivery_items` table.",
  },
  DELIVERY_ALREADY_FAVORITED: {
    en: "Delivery already favorited",
    zh: "交付物已收藏",
    description: "DLV_002. Idempotent favorite.",
  },

  // ─── Share ───
  SHARE_TOKEN_NOT_FOUND: {
    en: "Share token not found",
    zh: "分享令牌不存在",
    description: "Check `share_tokens` table.",
  },
  SHARE_TOKEN_EXPIRED: {
    en: "Share token expired",
    zh: "分享令牌已过期",
    description: "Check `share_tokens.expires_at`.",
  },

  // ─── E2EE Keys ───
  KEY_BACKUP_NOT_FOUND: {
    en: "Key backup not found",
    zh: "密钥备份不存在",
    description:
      "Check `encrypted_key_backups`. Possible corruption or never uploaded.",
  },
  KEY_ENUMERATION_BLOCKED: {
    en: "Public key enumeration blocked",
    zh: "公钥枚举被阻断",
    description: "Rate limiter on /keys/users/* — prevent attacker scanning.",
  },
  GROUP_KEY_VERSION_CONFLICT: {
    en: "Group key version conflict",
    zh: "群密钥版本冲突",
    description: "Two rotations raced. Check `group_keys.version`.",
  },
  GROUP_KEY_ROTATION_LIMIT: {
    en: "Group key rotation rate-limited",
    zh: "群密钥轮换被限流",
    description: "6/group/hour cap.",
  },
  PASSKEY_BACKUP_NOT_FOUND: {
    en: "Passkey (PRF) backup not found",
    zh: "Passkey (PRF) 备份不存在",
    description: "Check `passkey_backups` table.",
  },
  PASSKEY_BACKUP_ALREADY_EXISTS: {
    en: "Passkey backup already exists for this credential",
    zh: "该凭证的 Passkey 备份已存在",
    description: "Unique (user_id, credential_id).",
  },
  PASSKEY_BACKUP_DELETE_BLOCKED: {
    en: "Cannot delete last Passkey backup",
    zh: "不能删除最后一个 Passkey 备份",
    description: "Safety guard — user would lose PRF recovery.",
  },

  // ─── Security ───
  KEY_ROTATION_REENCRYPTION_FAILED: {
    en: "Key rotation re-encryption rollback",
    zh: "密钥轮换重新加密回滚",
    description: "SEC_003. Mid-rotation failure — check key_rotation_log.",
  },
  GROUP_ADMIN_ONLY_RESTRICTION: {
    en: "Group restricted to admin-only after key incident",
    zh: "密钥事件后群仅限管理员操作",
    description: "SEC_006. Incident response restriction.",
  },
  SAFETY_NUMBER_MISMATCH: {
    en: "Safety number mismatch",
    zh: "Safety Number 不匹配",
    description: "SEC_007. MITM warning — user manually rejected.",
  },
  PUBLIC_KEY_FETCH_FAILED: {
    en: "Public key fetch for E2EE session failed",
    zh: "获取 E2EE 会话公钥失败",
    description: "SEC_008. Target user has no published device key.",
  },
  USER_KEY_NOT_FOUND: {
    en: "Peer user has no published device public key",
    zh: "对方用户未发布设备公钥",
    description: "SEC_009. Agent introspection — user hasn't onboarded yet.",
  },

  // ─── Search ───
  INVALID_SEARCH_TYPE: {
    en: "Search 'types' parameter invalid",
    zh: "搜索 types 参数无效",
    description: "Must be 'actor' or 'artifact'.",
  },
  SEARCH_QUERY_TOO_SHORT: {
    en: "Search query too short",
    zh: "搜索关键词过短",
    description: "Minimum length enforced by validators.",
  },
  SEARCH_INDEX_CORRUPTED: {
    en: "Client-side search index corrupted",
    zh: "客户端搜索索引损坏",
    description: "SEARCH_001. Client must rebuild FTS index.",
  },
  SEARCH_OPFS_UNAVAILABLE: {
    en: "Client OPFS unavailable for search",
    zh: "客户端 OPFS 不可用",
    description: "SEARCH_002. Web-only — fallback to memory FTS.",
  },
  SEARCH_TIMEOUT: {
    en: "Search query timed out",
    zh: "搜索查询超时",
    description: "SEARCH_003. Client-side timeout — large dataset.",
  },

  // ─── Sync ───
  OFFLINE_SYNC_LIMIT_EXCEEDED: {
    en: "Offline message queue limit exceeded",
    zh: "离线消息队列已达上限",
    description: "SYNC_001. Queue cap per conversation.",
  },

  // ─── Translation ───
  TRANSLATION_API_UNAVAILABLE: {
    en: "Client translation API unavailable",
    zh: "客户端翻译 API 不可用",
    description: "TRANS_001. Platform translation not available.",
  },
  TRANSLATION_LANGUAGE_NOT_SUPPORTED: {
    en: "Target language not supported by translator",
    zh: "翻译目标语言不支持",
    description: "TRANS_002.",
  },

  // ─── STT ───
  STT_FILE_TOO_LARGE: {
    en: "Voice message file too large for STT",
    zh: "语音文件超出 STT 大小限制",
    description: "STT_001.",
  },
  STT_FORMAT_UNSUPPORTED: {
    en: "Voice format not supported by STT",
    zh: "语音格式不支持",
    description: "STT_002. Must be opus/m4a/wav.",
  },
  STT_TRANSCRIPTION_FAILED: {
    en: "Workers AI Whisper transcription failed",
    zh: "Workers AI Whisper 转写失败",
    description: "STT_003. Check AI binding + model availability.",
  },

  // ─── Migration ───
  MIGRATION_SESSION_EXPIRED: {
    en: "Data migration session expired",
    zh: "数据迁移会话已过期",
    description: "Check `migration_sessions` TTL.",
  },
  MIGRATION_ICE_TO_TURN: {
    en: "P2P migration fell back from ICE to TURN",
    zh: "P2P 迁移从 ICE 降级到 TURN",
    description: "MIGRATE_001. Informational — NAT traversal failed.",
  },
  MIGRATION_TURN_FAILED: {
    en: "TURN relay failed during migration",
    zh: "迁移 TURN 中继失败",
    description: "MIGRATE_002.",
  },
  MIGRATION_TRANSFER_INTERRUPTED: {
    en: "Migration transfer was interrupted",
    zh: "迁移传输被中断",
    description: "MIGRATE_003. Client should retry.",
  },
  MIGRATION_UNAUTHORIZED: {
    en: "Not authorized to perform migration",
    zh: "无权执行迁移",
    description: "MIGRATE_004. Session ownership mismatch.",
  },

  // ─── Draft ───
  DRAFT_KV_WRITE_FAILED: {
    en: "Draft KV write failed",
    zh: "草稿 KV 写入失败",
    description: "DRAFT_001. Check KV binding.",
  },
  DRAFT_SIZE_EXCEEDED: {
    en: "Draft exceeds size limit",
    zh: "草稿超出大小限制",
    description: "DRAFT_002.",
  },

  // ─── Media ───
  MEDIA_LOAD_FAILED: {
    en: "Media file load failed",
    zh: "媒体文件加载失败",
    description: "MEDIA_001. Client-side rendering error.",
  },

  // ─── Contact Card ───
  CARD_TARGET_NOT_FOUND: {
    en: "Contact card target user not found",
    zh: "联系人名片目标用户不存在",
    description: "CARD_001.",
  },

  // ─── Settings ───
  NOTIFICATION_SETTINGS_SAVE_FAILED: {
    en: "Notification settings save failed",
    zh: "通知设置保存失败",
    description: "SET_001.",
  },
  PRIVACY_SETTINGS_SAVE_FAILED: {
    en: "Privacy settings save failed",
    zh: "隐私设置保存失败",
    description: "SET_002.",
  },
  BIOMETRIC_UNAVAILABLE: {
    en: "Biometric auth unavailable on device",
    zh: "设备不支持生物识别",
    description: "SET_003. Device lacks Touch ID/Face ID capability.",
  },
  STORAGE_CLEANUP_FAILED: {
    en: "Local storage cleanup failed",
    zh: "本地存储清理失败",
    description: "SET_004.",
  },
  PREFERENCES_SAVE_FAILED: {
    en: "User preferences save failed",
    zh: "用户偏好保存失败",
    description: "SET_005.",
  },
  SETTING_VERSION_CONFLICT: {
    en: "Settings version conflict (concurrent write)",
    zh: "设置版本冲突 (并发写入)",
    description:
      "SET_006. Optimistic concurrency control — client should re-fetch + retry.",
  },

  // ─── Notifications ───
  PUSH_TOKEN_INVALID: {
    en: "APNs/FCM push token invalid",
    zh: "APNs/FCM 推送令牌无效",
    description: "NTF_001. Token expired or revoked by platform.",
  },
  DND_MODE_SILENT: {
    en: "DND mode — notification suppressed",
    zh: "勿扰模式 — 通知被静默",
    description: "NTF_002. Informational; not an error.",
  },
  MENTIONED_USER_NOT_FOUND: {
    en: "Mentioned user not found",
    zh: "@提及的用户不存在",
    description: "NTF_003. Dangling mention.",
  },
  NOTIFICATION_RESULT_INACCESSIBLE: {
    en: "Notification result inaccessible",
    zh: "通知结果无法访问",
    description: "NTF_004. Permission check failed after fan-out.",
  },
  DND_SYNC_FAILED: {
    en: "DND cross-device sync failed",
    zh: "勿扰跨设备同步失败",
    description: "NTF_005.",
  },
  SYSTEM_NOTIFICATION_NOT_FOUND: {
    en: "System notification not found",
    zh: "系统通知不存在",
    description: "NTF_006. Check `system_notifications` table.",
  },

  // ─── Remark ───
  REMARK_TOO_LONG: {
    en: "Friend remark exceeds length limit",
    zh: "好友备注超出长度限制",
    description: "REMARK_001.",
  },

  // ─── Combo validation ───
  COMBO_IMAGE_LIMIT: {
    en: "Combo message image count over limit",
    zh: "组合消息图片数量超限",
    description: "COMBO_001. Client-side validation.",
  },
  COMBO_INVALID_PAIR: {
    en: "Combo message type pairing invalid",
    zh: "组合消息类型配对无效",
    description: "COMBO_002. Client-side.",
  },
  COMBO_ARTIFACT_TEXT_ONLY: {
    en: "Artifact must only pair with text",
    zh: "Artifact 只能与文本组合",
    description: "COMBO_003. Client-side.",
  },
  COMBO_LINK_SOLO: {
    en: "Link must be sent as solo message",
    zh: "链接必须独立发送",
    description: "COMBO_004.",
  },
  COMBO_CONTACT_CARD_SOLO: {
    en: "Contact card must be sent as solo message",
    zh: "联系人名片必须独立发送",
    description: "COMBO_005.",
  },

  // ─── Rate Limit ───
  RATE_LIMIT_EXCEEDED: {
    en: "Rate limit exceeded",
    zh: "请求频率超限",
    description: "Check which limiter fired — see §1.3 in API Design.",
  },
  BACKPRESSURE_RETRY: {
    en: "Backpressure: service shedding load",
    zh: "服务过载保护中",
    description:
      "503 — backpressure middleware tripped on DB pool / queue depth / worker concurrency gauge. P1-B10 Viral-Ready. Check apps/api/src/middleware/backpressure.ts gauges + alert SQL templates.",
  },
  COST_GUARDRAIL_EXCEEDED: {
    en: "Cost guardrail exceeded",
    zh: "成本守护触发",
    description:
      "503 — rolling cost (min/hour/day) exceeded a configured ceiling. P1-B10 Viral-Ready. Check COST_GUARDRAIL_PER_* env vars and cost-guardrail-service.ts.",
  },

  // ─── Report ───
  INVALID_REPORT_TYPE: {
    en: "Report type invalid",
    zh: "举报类型无效",
    description: "Must match whitelist.",
  },
  REPORT_SUBMISSION_FAILED: {
    en: "Report submission failed",
    zh: "举报提交失败",
    description: "REPORT_001. DB write error.",
  },
  CONTENT_POLICY_VIOLATION: {
    en: "Public content rejected by proactive AI moderation",
    zh: "公开内容被主动 AI 审核拦截",
    description:
      "P1-6. Publish blocked by content-moderation gate (PUBLIC_CONTENT_MODERATION_ENABLED). details.category names the violation; check public_space_item_reports for the auto row.",
  },

  // ─── Investigation / Governance ───
  INVESTIGATION_NOT_FOUND: {
    en: "Investigation not found",
    zh: "调查不存在",
    description: "Check `investigations` table.",
  },
  INVESTIGATION_ALREADY_RESOLVED: {
    en: "Investigation already resolved",
    zh: "调查已结案",
    description: "Cannot re-approve a closed investigation.",
  },
  INVESTIGATION_NEEDS_TWO_APPROVERS: {
    en: "Investigation requires two independent approvers (four-eyes)",
    zh: "调查需要两名独立管理员审批 (四眼原则)",
    description:
      "Dual-approval governance — second distinct admin must approve.",
  },
  DUPLICATE_APPROVER: {
    en: "Same admin cannot approve twice",
    zh: "同一管理员不能重复审批",
    description:
      "Four-eyes guard — `investigation_approvals.admin_id` must differ.",
  },
  INVESTIGATION_SCOPE_TOO_BROAD: {
    en: "Investigation scope too broad",
    zh: "调查范围过宽",
    description: "GOV_001. Scope must target specific users/conversations.",
  },

  // ─── Media ───
  UNSUPPORTED_MEDIA_TYPE: {
    en: "Unsupported media type",
    zh: "不支持的媒体类型",
    description: "Content-Type header mismatch.",
  },

  // ─── Request Duplicates ───
  DUPLICATE_REQUEST: {
    en: "Duplicate request (idempotency)",
    zh: "重复请求",
    description: "Same idempotency key seen recently.",
  },

  // ─── Billing ───
  BILLING_NOT_FOUND: {
    en: "Billing record not found",
    zh: "计费记录不存在",
    description: "BILL_001. Check `token_ledger` / `subscriptions` tables.",
  },
  INSUFFICIENT_TOKENS: {
    en: "Insufficient tokens — user needs to top up",
    zh: "Token 余额不足",
    description: "BILL_002. User's `current_balance` < required cost.",
  },
  BILLING_PROVIDER_UNAVAILABLE: {
    en: "Payment provider unavailable for this platform",
    zh: "该平台支付通道不可用",
    description:
      "Provider-neutral billing gate. Check platform rail flags for Stripe, StoreKit, Play Billing, or fake/admin grant provider.",
  },
  PAYMENT_PROVIDER_PENDING: {
    en: "Payment provider account pending",
    zh: "支付通道账号待开通",
    description:
      "Provider exists but is not production-ready. Do not expose paid launch flows until native/web provider approval is complete.",
  },
  AGENT_ENTITLEMENT_REQUIRED: {
    en: "Agent access requires entitlement",
    zh: "Agent 访问需要权益",
    description:
      "Paid or protected Agent request without an active entitlement. Check entitlement_events and H2A relation state.",
  },
  COMMERCE_ACTION_NOT_ALLOWED: {
    en: "H2A commerce action not allowed",
    zh: "H2A 商业动作不允许",
    description:
      "Client action was missing from the commerce allow-list or capability manifest scope. Check H2A artifact subtype and endpoint gate.",
  },
  USAGE_EVENT_REPLAYED: {
    en: "Usage event replayed as no-op",
    zh: "用量事件重复回放",
    description:
      "Idempotent usage event replay. Should not debit tokens again; inspect producer idempotency key if unexpected.",
  },
  USAGE_EVENT_NOT_FOUND: {
    en: "Usage event not found",
    zh: "用量事件不存在",
    description:
      "Usage finalization referenced a missing `usage_events.id`. Check idempotency and producer logs.",
  },
  USAGE_EVENT_VOIDED: {
    en: "Usage event is voided",
    zh: "用量事件已作废",
    description:
      "Usage finalization attempted to bill a voided event. Check usage replay and adjustment flow.",
  },
  PAYMENT_TRANSACTION_REPLAYED: {
    en: "Payment transaction replayed as no-op",
    zh: "支付交易重复回放",
    description:
      "Provider transaction idempotency replay. EntitlementService should return prior transaction/event without double-crediting.",
  },
  STRIPE_ERROR: {
    en: "Stripe API error",
    zh: "Stripe API 错误",
    description: "BILL_003. Check Stripe dashboard + webhook replay.",
  },
  BILLING_WEBHOOK_INVALID: {
    en: "Stripe webhook signature invalid",
    zh: "Stripe webhook 签名无效",
    description: "BILL_004. Check STRIPE_WEBHOOK_SECRET in wrangler.toml.",
  },
  BILLING_PRICE_NOT_SEEDED: {
    en: "Stripe prices not seeded",
    zh: "Stripe 价格目录未初始化",
    description:
      "BILL_005. Run POST /billing/price-seed from admin console before checkout.",
  },

  // ─── L2 Cloud Sync ───
  STORAGE_QUOTA_EXCEEDED: {
    en: "L2 cloud storage quota exceeded",
    zh: "L2 云存储配额已满",
    description: "L2_001. User needs to upgrade plan or delete archives.",
  },
  L2_SYNC_DISABLED: {
    en: "L2 cloud sync disabled for this conversation",
    zh: "该会话未启用 L2 云同步",
    description: "L2_002. Check `l2_sync_settings` per-user.",
  },
  ARCHIVE_NOT_FOUND: {
    en: "L2 archive not found",
    zh: "L2 归档不存在",
    description: "L2_003. Check `message_archives` table.",
  },
  ARCHIVE_DOWNLOAD_FAILED: {
    en: "L2 archive download from R2 failed",
    zh: "L2 归档下载失败",
    description: "L2_004.",
  },
  CLOUD_PREVIEW_QUOTA_EXCEEDED: {
    en: "Cloud preview quota exceeded",
    zh: "云端预览配额超限",
    description:
      "CloudPreviewSession runtime/storage quota exceeded. Check workspace quota, runtime seconds, and cleanup jobs.",
  },
  CLOUD_PREVIEW_SESSION_EXPIRED: {
    en: "Cloud preview session expired",
    zh: "云端预览会话已过期",
    description:
      "Preview handle expired and must be restored or reopened. Check session TTL and ownership.",
  },

  // ─── AppData ───
  APPDATA_GRANT_REQUIRED: {
    en: "AppData operation missing explicit grant",
    zh: "AppData 缺少授权",
    description:
      "Platform-visible AppData read/write was attempted without an active app_data_grants row. Check install consent, grant scope, and revoked_at.",
  },
  APPDATA_OP_REPLAY_MISMATCH: {
    en: "AppData op_id replay digest mismatch",
    zh: "AppData 操作回放不匹配",
    description:
      "Duplicate AppData op_id arrived with a different digest. Treat as corrupt client state or replay attempt; inspect AppDataGateway request logs.",
  },
  APPDATA_OP_REVISION_AHEAD: {
    en: "AppData client revision is ahead of server",
    zh: "AppData 客户端版本超前",
    description:
      "Sync op declared base_revision greater than AppDataDO cloud_revision. Client local state may be corrupt or using an incompatible manifest.",
  },
  APPDATA_QUOTA_EXCEEDED: {
    en: "AppData quota exceeded",
    zh: "AppData 配额超限",
    description:
      "AppData read/write/storage quota rejected the operation. Check usage events, installation quota, and shared-installation budget.",
  },
  APPDATA_SCHEMA_REJECTED: {
    en: "AppData manifest schema rejected payload",
    zh: "AppData 数据结构校验失败",
    description:
      "Payload failed the collection manifest schema before reaching AppDataDO. Check app_version_id, field paths, and package manifest hash.",
  },
  APPDATA_POLICY_REJECTED: {
    en: "AppData policy rejected operation",
    zh: "AppData 策略拒绝操作",
    description:
      "Operation violates manifest classification, shared-install membership, grant scope, or server-authoritative collection policy.",
  },
  APPDATA_MANIFEST_HASH_MISMATCH: {
    en: "AppData manifest hash mismatch",
    zh: "AppData 清单哈希不匹配",
    description:
      "Client attempted sync/query with a package manifest hash that differs from the grant-authorized hash. Re-authorization is required.",
  },
  APPDATA_TRANSACTION_NESTED: {
    en: "Nested AppData transaction attempted",
    zh: "AppData 嵌套事务被拒绝",
    description:
      "Generated app or runtime attempted transaction() inside an active AppData transaction. Refactor into one callback or appendEvent workflow.",
  },
  APPDATA_OPERATOR_UNSUPPORTED: {
    en: "Unsupported AppData query operator",
    zh: "AppData 查询操作符暂不支持",
    description:
      "Query used an operator outside the currently enabled AppData phase. Check AppDataQuery validator and manifest index type tags.",
  },
  APPDATA_PACKAGE_HASH_MISMATCH: {
    en: "AppData package hash mismatch",
    zh: "AppData 包哈希不匹配",
    description:
      "Published app package bytes do not match the manifest/content-addressed hash. Block install and inspect package-store integrity.",
  },
  APPDATA_PACKAGE_NOT_FOUND: {
    en: "AppData package not found",
    zh: "AppData 包不存在",
    description:
      "Requested app package metadata or object is missing. Check the package registry row, KV metadata cache, and R2 object key.",
  },
  APPDATA_PACKAGE_IMMUTABLE: {
    en: "Published AppData package is immutable",
    zh: "已发布 AppData 包不可变更",
    description:
      "Attempted to mutate an already published app package version. Create a new immutable package version instead.",
  },
  APPDATA_PACKAGE_SCHEMA_REJECTED: {
    en: "AppData package schema rejected",
    zh: "AppData 包结构校验失败",
    description:
      "Package manifest failed AppData schema validation. Check manifest classification literals, indexes, permissions, and size caps.",
  },
  APPDATA_PACKAGE_SIGNATURE_INVALID: {
    en: "AppData package signature invalid",
    zh: "AppData 包签名无效",
    description:
      "Package signature did not verify against the declared package signing public key. Block publish/install and inspect app package provenance.",
  },
  APPDATA_PACKAGE_SIGNING_KEY_MISMATCH: {
    en: "AppData package signing key mismatch",
    zh: "AppData 包签名密钥不匹配",
    description:
      "Package signing public key differs from the persistent app-level trust registry. Treat as package provenance failure unless an explicit key rotation flow is approved.",
  },
  APPDATA_PACKAGE_SHARE_NOT_FOUND: {
    en: "AppData package share link not found",
    zh: "AppData 包分享链接不存在",
    description:
      "Protected package share lookup could not find an active share row. Check share token TTL, revocation state, and package visibility.",
  },
  APPDATA_PACKAGE_SHARE_FORBIDDEN: {
    en: "AppData package share creation forbidden",
    zh: "AppData 包分享创建被拒绝",
    description:
      "Caller tried to create a protected package share without owning the package or holding the required package grant. Check app_id ownership and app_data_grants.",
  },
  APPDATA_PACKAGE_NOT_SHAREABLE: {
    en: "AppData package is not shareable",
    zh: "AppData 包不可分享",
    description:
      "Package visibility/kind does not allow protected share links. Check package visibility, package_kind, and product policy for shareable apps.",
  },

  // ─── Agent Marketplace / Creator Economy ───
  LISTING_NOT_PUBLISHED: {
    en: "Agent listing is not published",
    zh: "Agent 上架页未发布",
    description:
      "Discover/public listing request hit a private, protected, draft, or unpublished listing. Check agent_listings.listing_status.",
  },
  AGENT_LISTING_REVIEW_REQUIRED: {
    en: "Agent listing requires review",
    zh: "Agent 上架需要审核",
    description:
      "Public or paid listing is waiting for marketplace review. Check listing review workflow and governance status.",
  },
  CREATOR_PAYOUT_NOT_READY: {
    en: "Creator payout setup is not ready",
    zh: "创作者收款未就绪",
    description:
      "Paid activation blocked because creator account is not payout_ready. Check Stripe Connect/KYC state.",
  },

  // ─── Grant ───
  GRANT_NOT_FOUND: {
    en: "Data access grant not found",
    zh: "数据授权不存在",
    description: "GNT_001. Check `grants` table.",
  },
  GRANT_REVOKED: {
    en: "E2EE message grant has been revoked",
    zh: "E2EE 消息授权已被撤销",
    description:
      "E2EE-UNI-V1 §5.4. `message_grants.revoked_at IS NOT NULL`. Agent retry yields 403. Check `grant_access_log` for revocation event.",
  },
  GRANT_EXPIRED: {
    en: "E2EE message grant has expired",
    zh: "E2EE 消息授权已过期",
    description:
      "E2EE-UNI-V1 §5.4. `message_grants.expires_at < now()`. Owner must re-grant via POST /messages/:id/grants.",
  },
  GRANT_MARKED_STALE: {
    en: "Grant marked stale by recipient IK rotation",
    zh: "授权因接收方密钥轮换被标记过期",
    description:
      "Rev. 7 §4.3 SYS-4. `message_grants.marked_stale_at IS NOT NULL`. Sender's client must refetch peer prekey bundle + re-wrap CEK + re-POST. Check `audit_records.action = 'admin.user.rotate_identity'` for the triggering event.",
  },
  GRANT_STALE_SENDER: {
    en: "Wrap rejected: sender used stale recipient IK fingerprint",
    zh: "发送方使用了过期的接收方 IK 指纹",
    description:
      "Rev. 7 §4.3 SYS-4. Server rejected wrap at `POST /conversations/:id/messages` because `recipient_ik_fp` did not match server-current `identity_keys.ik_x25519_public_key`. Client-side send-orchestrator retries once with fresh bundle; a 2nd 410 is terminal.",
  },
  IDEMPOTENCY_REPLAY: {
    en: "X-Idempotency-Key replay on admin rotation endpoint",
    zh: "管理员轮换接口幂等键重放",
    description:
      "Rev. 7 §4.8 A3-OP. Same X-Idempotency-Key seen twice within 24h on POST /admin/agents/:id/rotate-identity. Confirm prior rotation via audit log OR send a fresh idempotency key.",
  },
  BACKUP_STALE_IK: {
    en: "Passkey backup_b blob pinned to a retired IK",
    zh: "Passkey 备份绑定到已过期的 IK",
    description:
      "Rev. 7 §4.12 SYS-10. `passkey_backup_entries.ik_ed25519_fingerprint` does not match server-current `identity_keys.ik_ed25519_public_key`. Root cause: a prior IK rotation's backup re-wrap loop failed partway. User must run rotation-resume flow.",
  },
  MISSING_WRAPS_GRANTS: {
    en: "Agent reply missing wraps_grants for h2a recipient",
    zh: "Agent 回复缺失 wraps_grants",
    description:
      "E2EE invariant (2026-04-30). Agent POSTed `/agents/:id/conversations/:cid/messages` (or sent the equivalent WS frame) without a `wraps_grants` entry addressed to the conversation's user member. Server rejected with 400 to prevent persisting a message no recipient can decrypt. Root cause is almost always a buggy agent SDK / plugin that builds the encrypted payload but forgets the per-recipient grant. Fix: ensure the SDK build path includes `wraps_grants: [{ grantee_id: <user_id>, grantee_type: 'user', wrap_envelope: <wrap> }]` and the matching `aad_epoch`.",
  },
  PREKEY_BUNDLE_NOT_FOUND: {
    en: "User has not uploaded a prekey bundle",
    zh: "用户尚未上传预密钥包",
    description:
      "E2EE-UNI-V1 §5.1. Check `identity_keys` table. User's mobile client must run prekey-manager bootstrap.",
  },
  SPK_SIGNATURE_INVALID: {
    en: "Signed prekey Ed25519 signature verification failed",
    zh: "签名预密钥 Ed25519 签名校验失败",
    description:
      "E2EE-UNI-V1 §5.1. SPK upload rejected because signature doesn't verify against user's IK_ed25519. Client bug or tampered upload.",
  },
  PREKEY_BATCH_LIMIT: {
    en: "Prekey batch request exceeded size limit",
    zh: "预密钥批量请求超出尺寸限制",
    description:
      "GNT_008. POST /v1/keys/prekey-bundle/batch accepts ≤100 user_ids (V-6 viral handshake guard). Client must split into multiple batches.",
  },
  IDENTITY_KEY_LOCKED: {
    en: "Identity key already exists for this user",
    zh: "该用户已存在身份密钥",
    description:
      "First-write-wins on `identity_keys`. A new device tried to PUT /prekeys/identity with a different ik_x25519 / ik_ed25519 from the stored row. Client must restore the original keypair from /keys/backup instead of generating fresh ones.",
  },
  IDENTITY_PROOF_INVALID: {
    en: "Identity binding proof failed verification",
    zh: "身份绑定证明校验失败",
    description:
      'E2EE-UNI-V1 §H2 (2026-04-17 crypto review). PUT /prekeys/identity supplied an `identity_proof` that Ed25519-verify rejected against the declared IK pair. Client bug, tampered upload, or directory-swap attack attempt — proof must be Ed25519(ik_ed25519_sk) over (ik_x25519_public || "hashee-identity-binding-v1").',
  },
  IDENTITY_PROOF_MISSING: {
    en: "Identity binding proof required but not supplied",
    zh: "缺少必需的身份绑定证明",
    description:
      "E2EE-UNI-V1 §H2 + T-5B-3. Staging flip of REQUIRE_IDENTITY_PROOF made `identity_proof` mandatory on PUT /prekeys/identity; a client built against the pre-flag version uploaded without it. Upgrade client or flip flag back if a legacy client still needs a grace window.",
  },
  JUSTIFICATION_TOO_SHORT: {
    en: "Admin audit justification is too short (minimum 20 chars)",
    zh: "运维审计理由过短（最少 20 字符）",
    description:
      "R5-L4 (E2EE V1.1 Wave 1C). POST /admin/agents/:id/rotate-identity and future admin rotation endpoints require an `X-Hashee-Audit-Reason` header (and matching body `justification`) at least 20 chars long. Shorter text is rejected so the forensic trail has real content rather than a placeholder.",
  },
  JUSTIFICATION_MISSING: {
    en: "Admin audit justification header is missing",
    zh: "缺少运维审计理由 header",
    description:
      "R5-L4. Operator issued a rotation call without the `X-Hashee-Audit-Reason` header. The header is mandatory so Cloudflare edge logs capture the reason even if the request body is mutated later in the pipeline.",
  },
  IDENTITY_BINDING_ALREADY_CURRENT: {
    en: "Rotation target IK is identical to current IK (no-op)",
    zh: "目标身份密钥与当前一致，无需轮换",
    description:
      "R5-L4. Admin agent rotation rejected because the submitted `new_ik_x25519_public_key` / `new_ik_ed25519_public_key` match the current stored row byte-for-byte. Prevents accidental replays from wiping grants without a real key change. Generate a fresh keypair and retry.",
  },
  EPOCH_STALE: {
    en: "Message envelope bound to a stale conversation epoch",
    zh: "消息信封绑定了过期的会话 epoch",
    description:
      "E2EE-UNI-V1 §2.1 / D-Crypto-11. Sender composed Layer 1 AAD with an epoch older than the server's current row. Usually means the shell skipped a GET /conversations refresh after a group-key rotation; client retries once after re-syncing.",
  },

  // ─── Export ───
  EXPORT_NOT_FOUND: {
    en: "Export job not found",
    zh: "导出任务不存在",
    description: "EXP_001. Check `exports` table.",
  },
  EXPORT_RATE_LIMITED: {
    en: "Export creation rate limited",
    zh: "导出任务创建被限流",
    description: "EXP_002. Per-user cap.",
  },
  EXPORT_NOT_READY: {
    en: "Export not ready yet",
    zh: "导出任务尚未就绪",
    description: "EXP_003. Still processing — poll later.",
  },
  EXPORT_EXPIRED: {
    en: "Export expired",
    zh: "导出已过期",
    description: "EXP_004. Download window TTL reached.",
  },

  // ─── Tool / Capability Manifest ───
  TOOL_PLATFORM_ERROR: {
    en: "Tool execution failed on the remote platform",
    zh: "工具在远端平台执行失败",
    description:
      "A2H tool runner reached the host platform but the platform itself failed the call. Check agent webhook logs, upstream tool provider logs, and remote sandbox/runtime health rather than the Hashee API route.",
  },
  TOOL_INVALID_ARGUMENTS: {
    en: "Tool call arguments failed validation",
    zh: "工具调用参数校验失败",
    description:
      "Capability manifest or invocation payload shape did not match the tool schema. Check manifest version, JSON field names, and server-side validator errors returned with the tool result.",
  },
  TOOL_UNAVAILABLE: {
    en: "Requested tool is unavailable",
    zh: "请求的工具当前不可用",
    description:
      "The tool was disabled, not installed, or the provider was offline at dispatch time. Check the agent capability manifest, current tool registration state, and provider availability.",
  },
  TOOL_NOT_DECLARED: {
    en: "Tool call references an undeclared tool",
    zh: "工具未在清单中声明",
    description:
      "Stage 3 Phase D2 dispatcher rejected a tool_call because `tool_name` was absent from `capability_manifest.tools[]`. Check the agent manifest stored on the relation and the emitted tool_call payload.",
  },
  TOOL_SCOPE_NOT_GRANTED: {
    en: "Tool call scope was not granted",
    zh: "工具权限范围未授权",
    description:
      "The requested `permission_scope` is not present in the H2A relation's granted scopes. Check permission grants, relation state, and capability manifest scope IDs.",
  },
  TOOL_NOT_SUPPORTED_IN_GROUP: {
    en: "Tool calls are not supported in groups",
    zh: "群聊不支持工具调用",
    description:
      "V1 blocks tool_call execution inside group conversations. Verify the conversation type and ensure the agent replies with a denied tool_response rather than retrying.",
  },
  TOOL_USER_REFUSED: {
    en: "User refused the tool consent prompt",
    zh: "用户拒绝工具授权",
    description:
      "A medium/high sensitivity tool requested consent and the user denied it. This is an expected policy outcome; check audit logs only if refusal frequency is abnormal.",
  },
  TOOL_USER_TIMEOUT: {
    en: "Tool consent prompt timed out",
    zh: "工具授权确认超时",
    description:
      "A high-sensitivity tool prompt was not confirmed within the configured timeout window. Check client foreground state and notification/consent UI telemetry.",
  },

  // ─── Compliance / Sanctions ───
  REGION_UNAVAILABLE: {
    en: "Registration blocked by region policy",
    zh: "地区合规策略拦截注册",
    description:
      "Registration rejected by sanctions/availability middleware. Check `sanctions_audit_log` for the exact country/subdivision reason; do not expose the matched list to the user.",
  },

  // ─── Generic ───
  INTERNAL_ERROR: {
    en: "Internal server error",
    zh: "服务器内部错误",
    description: "Check Worker logs for stack trace.",
  },
  VALIDATION_ERROR: {
    en: "Request validation failed",
    zh: "请求参数校验失败",
    description: "Zod schema rejection. Check response details for field path.",
  },
  INVALID_INPUT: {
    en: "Invalid input (DO lightweight validation)",
    zh: "输入无效（DO 轻量校验）",
    description:
      "DO 400 — boolean-style required-field / format checks distinct from VALIDATION_ERROR (which carries Zod detail). Used by contact-list, user-profile, identity-shard, delivery-shard, auth-credential DOs.",
  },
  USER_ID_REQUIRED: {
    en: "user_id parameter missing or malformed",
    zh: "user_id 参数缺失或格式错误",
    description: "DO 400 — used by contact-list-do, user-profile-do.",
  },
  NOT_FOUND: {
    en: "Resource not found (generic)",
    zh: "资源不存在",
    description: "Generic 404 fallback.",
  },
  FORBIDDEN: {
    en: "Forbidden (generic permission denial)",
    zh: "无权限 (通用)",
    description: "Generic 403 fallback.",
  },
  METHOD_NOT_ALLOWED: {
    en: "HTTP method not allowed",
    zh: "HTTP 方法不允许",
    description: "Verify route supports this verb.",
  },
  SERVICE_UNAVAILABLE: {
    en: "Service temporarily unavailable",
    zh: "服务暂时不可用",
    description: "Upstream dependency failing (Neon / R2 / KV).",
  },
  NOT_IMPLEMENTED: {
    en: "This action is not available yet",
    zh: "该操作尚未实现",
    description:
      "The route exists but its backing pipeline does not. Currently emitted by admin delivery reindex — there is no delivery search index / reindex queue, so the endpoint reports honestly instead of faking success.",
  },
  INVALID_FAULT_INJECTION: {
    en: "Malformed x-hashee-fault-inject header",
    zh: "x-hashee-fault-inject 请求头格式错误",
    description:
      "FU#7 fault-injection middleware (non-production only) rejected a malformed `x-hashee-fault-inject` header. Header format: `<fault-type>:<probability>` where fault-type ∈ {postgres-step-3, do-commit, kv-miss, queue-send} and probability ∈ [0, 1]. Production hard-blocks the entire middleware before header parsing, so this code should NEVER surface in production logs. If it does → an environment misclassification bug.",
  },
  MIME_NOT_ALLOWED: {
    en: "MIME type not allowed for media kind",
    zh: "媒体类型不在该 media_kind 的允许列表中",
    description:
      "Explore W1 — POST /space/objects/presign or /space/items/publish rejected because content_type is not in PUBLIC_MIME_ALLOWLIST[media_kind]. Whitelist is in packages/internal/config/src/public-media.ts. If users hit this, the picker should pre-filter MIMEs.",
  },
  SIZE_TOO_LARGE: {
    en: "File size exceeds the per-kind cap",
    zh: "文件大小超过该 media_kind 的上限",
    description:
      "Explore W1 — POST /space/objects/presign rejected because size > PUBLIC_OBJECT_SIZE_CAP_BYTES[media_kind] (image 10M / video 100M / audio 50M / file 50M / artifact 5M).",
  },
  MIME_KIND_MISMATCH: {
    en: "storage_object content_type does not match publish media_kind",
    zh: "storage_object 的 content_type 与 publish 的 media_kind 不匹配",
    description:
      "Explore W1 — POST /space/items/publish rejected because storage_objects.content_type is not in PUBLIC_MIME_ALLOWLIST[media_kind] for the publish body's media_kind. Indicates client uploaded under one kind and tries to publish under another.",
  },
  STORAGE_PLAN_QUOTA_EXCEEDED: {
    en: "Storage plan quota exceeded",
    zh: "存储配额已用尽",
    description:
      "Explore W1 — POST /space/objects/presign rejected because billable_bytes (sum across billable storage_objects in pending/finalizing/confirmed/pending_delete) + new_size > user_storage_plans.storage_limit_bytes. User must upgrade plan or wait for cleanup.",
  },
  PENDING_QUOTA_EXCEEDED: {
    en: "Too many published-object uploads in flight",
    zh: "进行中的发布上传过多",
    description:
      "Explore W1 — POST /space/objects/presign rejected because per-user pending cap exceeded: 5 published-object storage_objects in (pending|finalizing) OR pending bytes + new_size > 500MB. Burst-spam protection — clears as cleanup cron drains pending rows.",
  },
  HEAD_VALIDATION_FAILED: {
    en: "tmp R2 object did not match presign declaration",
    zh: "tmp R2 对象与 presign 声明不一致",
    description:
      "Explore W1 — POST /space/objects/:id/confirm aborted because R2 HEAD on tmp_r2_key returned size/Content-Type that don't match the storage_objects.size_bytes / content_type declared at presign time. Storage object is now status='aborted', abort_reason='head_validation_failed'. Client must restart upload.",
  },
  COPY_IF_MATCH_FAILED: {
    en: "tmp R2 object changed mid-confirm (etag mismatch)",
    zh: "确认上传期间 tmp R2 对象被修改",
    description:
      "Explore W1 — POST /space/objects/:id/confirm aborted because S3 CopyObject x-amz-copy-source-if-match returned 412. Means tmp_r2_key was re-PUT between HEAD and copy. Storage object aborted. Client must restart.",
  },
  CONFIRM_IN_PROGRESS: {
    en: "Another confirm is finalizing this storage object",
    zh: "该 storage_object 正在确认中,请稍后重试",
    description:
      "Explore W1 — POST /space/objects/:id/confirm returned 202 because status='finalizing' and another worker (or cron sweep) is mid-copy. Client should poll per Retry-After header (FINALIZING_TIMEOUT_SECONDS/2).",
  },
  PENDING_EXPIRED: {
    en: "Presign URL TTL expired before confirm",
    zh: "presign URL 在 confirm 前已过期",
    description:
      "Explore W1 — POST /space/objects/:id/confirm returned 410 because now > pending_cleanup_deadline. Client took too long between presign and confirm. Storage object aborted; tmp R2 swept by cleanup cron.",
  },
  OBJECT_ABORTED: {
    en: "storage_object is in aborted state",
    zh: "storage_object 已 aborted",
    description:
      "Explore W1 — confirm or publish referenced a storage_object whose status='aborted'. Caused by earlier head/copy/timeout failure. Client must restart upload from presign.",
  },
  OBJECT_DELETED: {
    en: "storage_object is pending_delete or deleted",
    zh: "storage_object 已删除",
    description:
      "Explore W1 — confirm or publish referenced a storage_object whose status='pending_delete' or 'deleted'. Either GDPR cascade, takedown, or publisher delete; storage row no longer usable.",
  },
  OBJECT_ALREADY_PUBLISHED: {
    en: "storage_object is already bound to a published item",
    zh: "该 storage_object 已绑定到 public_space_items",
    description:
      "Explore W1 — POST /space/items/publish rejected because partial unique index uq_public_space_items_storage_object_active blocks rebinding. One storage_object → one published item; client should re-presign for a fresh upload if it wants to re-publish.",
  },
  MISSING_FIELD: {
    en: "Required field missing for the chosen media_kind",
    zh: "选定的 media_kind 缺少必填字段",
    description:
      "Explore W1 — POST /space/items/publish rejected per superRefine cardinality: media_kind='text' needs body_text; 'link' needs link_url; image/video/audio/file/artifact need storage_object_id.",
  },
  PUBLISH_DISABLED: {
    en: "Publish flow disabled by kill switch",
    zh: "发布功能已通过 kill switch 关闭",
    description:
      "Explore W1 — POST /space/objects/presign returned 503 because Worker secret ROUTE_C_PUBLISH_ENABLED='false'. Used to drain in-flight uploads during incident rollback. Confirm + publish + /media still work for already-confirmed objects; only new presign is blocked.",
  },
  // ── Slice F — Standalone App Platform (2026-05-27) ────────────────────
  SAP_NOT_PROVISIONED: {
    en: "Standalone App Platform not provisioned in this environment",
    zh: "本环境尚未配置独立应用平台",
    description:
      "Slice F — agent-deployments route returned 503 because CF_ACCOUNT_ID / WFP_NAMESPACE_NAME / WFP_API_TOKEN / APP_SOURCE_BUCKET binding missing. Provision via wrangler secret put + wrangler.toml binding.",
  },
  SAP_SOURCE_INVALID: {
    en: "Upload body missing `source` string field",
    zh: "上传请求缺少 `source` 字符串字段",
    description:
      "Slice F — POST /agent-deployments/:id/source body must be JSON `{source:<esm-js>}`. Rejected at 400.",
  },
  SAP_AGENT_TYPE_INVALID: {
    en: "Agent is not a hosted standalone app",
    zh: "Agent 不是托管独立应用",
    description:
      "Slice F — deployment endpoints only accept agent_type=standalone_app and deployment_target=hashee_hosted.",
  },
  SAP_SOURCE_EMPTY: {
    en: "Empty source bundle",
    zh: "源代码为空",
    description: "Slice F — zero-length source rejected at 400.",
  },
  SAP_SOURCE_TOO_LARGE: {
    en: "Source exceeds 1 MiB cap",
    zh: "源代码超过 1 MiB 上限",
    description: "Slice F — uploaded source > SOURCE_MAX_BYTES. Returned 413.",
  },
  SAP_VERSION_INVALID: {
    en: "Version param must be a positive integer",
    zh: "版本参数必须是正整数",
    description: "Slice F — non-positive or non-integer version param.",
  },
  SAP_DEPLOYMENT_NOT_FOUND: {
    en: "Deployment row not found for (agent_id, version)",
    zh: "未找到对应版本的部署记录",
    description:
      "Slice F — deploy targeted a version that has no agent_deployments row.",
  },
  SAP_SOURCE_OBJECT_MISSING: {
    en: "R2 source object disappeared",
    zh: "R2 源代码对象丢失",
    description:
      "Slice F — deploy step couldn't find the uploaded bytes in APP_SOURCE_BUCKET.",
  },
  SAP_SCAN_REJECTED: {
    en: "Static scan flagged forbidden patterns",
    zh: "静态扫描发现禁止的模式",
    description:
      "Slice F — source contained eval / env.AI / env.HYPERDRIVE / fetch hashee.ai. Row → status='failed'.",
  },
  SAP_WFP_UPLOAD_FAILED: {
    en: "Cloudflare Workers for Platforms upload failed",
    zh: "Cloudflare Workers for Platforms 上传失败",
    description:
      "Slice F — CF returned non-2xx on PUT /workers/dispatch/namespaces/:ns/scripts/:script. Retry after the underlying CF issue clears.",
  },
  SAP_AGENT_TOKEN_REQUIRED: {
    en: "Hosted app deployment requires the agent token",
    zh: "托管 App 部署需要 Agent token",
    description:
      "Slice F — first deploy must provide agent_token so WfP can expose env.AGENT_TOKEN to the hosted runtime.",
  },
  SAP_SECRET_KEY_INVALID: {
    en: "Secret key must match /^[A-Z][A-Z0-9_]{0,63}$/",
    zh: "secret key 必须匹配 /^[A-Z][A-Z0-9_]{0,63}$/",
    description:
      "Slice F — secret key violates the Worker env var naming convention.",
  },
  SAP_SECRET_VALUE_INVALID: {
    en: "Secret value missing or wrong type",
    zh: "secret 值缺失或类型错误",
    description:
      "Slice F — PUT /agent-deployments/:id/secrets/:key body must be `{value:<string>}`.",
  },
  SAP_SECRET_VALUE_EMPTY: {
    en: "Empty secret value",
    zh: "secret 值为空",
    description: "Slice F — zero-length secret value rejected at 400.",
  },
  SAP_SECRET_DEPLOYMENT_REQUIRED: {
    en: "Deploy the hosted app before setting runtime secrets",
    zh: "设置运行时 secret 前必须先部署托管 App",
    description:
      "Slice F — CF script secrets are attached to an existing WfP script; pre-deploy hash-only rows are rejected.",
  },
  SAP_SECRET_RESEED_REQUIRED: {
    en: "Runtime secrets must be re-set for this deployment",
    zh: "此部署需要重新设置运行时 secret",
    description:
      "Slice F — CF secret values cannot be read back and inherited across different script names. Re-submit the secret value.",
  },
  SAP_LOGS_NOT_AVAILABLE: {
    en: "Logs are not available — AGENT_LOGS_BUCKET binding is not provisioned for this environment.",
    zh: "日志功能尚未配置 — 当前环境未绑定 AGENT_LOGS_BUCKET。",
    description:
      "Slice F live-logs — GET /agent-deployments/:id/deployments/:dep_id/logs returns 501 when AGENT_LOGS_BUCKET R2 binding is unset.",
  },
  SAP_LOG_LIMIT_INVALID: {
    en: "?limit query parameter must be an integer in 1..200.",
    zh: "?limit 参数必须是 1..200 之间的整数。",
    description:
      "Slice F live-logs — invalid ?limit on /agent-deployments/:id/deployments/:dep_id/logs.",
  },
};

const DEFAULT_ENTRY: AdminErrorEntry = {
  en: "Unknown error code",
  zh: "未知错误码",
  description:
    "Code not in admin-error-messages.ts — add entry when new codes ship.",
};

const HTTP_FALLBACK_ERROR_MESSAGES: Record<string, AdminErrorEntry> = {
  HTTP_400: {
    en: "Bad admin API request",
    zh: "后台请求参数错误",
    description:
      "API returned HTTP 400 without a structured error body. Inspect the request payload and query parameters in the Network panel.",
  },
  HTTP_401: {
    en: "Admin session is unauthorized",
    zh: "后台登录态无效",
    description:
      "API returned HTTP 401 without a structured error body. Re-login and check auth/refresh behavior.",
  },
  HTTP_403: {
    en: "Admin permission denied",
    zh: "后台权限不足",
    description:
      "API returned HTTP 403 without a structured error body. Verify the account is in ADMIN_USER_IDS and the route uses admin middleware.",
  },
  HTTP_404: {
    en: "Admin API route not found",
    zh: "后台接口不存在",
    description:
      "API returned HTTP 404 without a structured error body. Check the frontend path, backend /admin mount, and deploy version.",
  },
  HTTP_409: {
    en: "Admin API conflict",
    zh: "后台请求状态冲突",
    description:
      "API returned HTTP 409 without a structured error body. Check optimistic locks, idempotency keys, or current resource state.",
  },
  HTTP_422: {
    en: "Admin API validation failed",
    zh: "后台请求校验失败",
    description:
      "API returned HTTP 422 without a structured error body. Check form fields and request body shape.",
  },
  HTTP_429: {
    en: "Admin API rate limit exceeded",
    zh: "后台请求触发限流",
    description:
      "API returned HTTP 429 without a structured error body. Check adminRateLimiter and retry after the limiter window.",
  },
  HTTP_500: {
    en: "Admin API internal error",
    zh: "后台接口内部错误",
    description:
      "API returned HTTP 500 without a structured error body. Check Worker logs and request id.",
  },
  HTTP_502: {
    en: "Admin API upstream gateway error",
    zh: "后台上游网关错误",
    description:
      "API returned HTTP 502 without a structured error body. Check upstream service and Cloudflare edge logs.",
  },
  HTTP_503: {
    en: "Admin API temporarily unavailable",
    zh: "后台接口暂不可用",
    description:
      "API returned HTTP 503 without a structured error body. Check service health and deployment status.",
  },
  HTTP_504: {
    en: "Admin API gateway timeout",
    zh: "后台接口网关超时",
    description:
      "API returned HTTP 504 without a structured error body. Check backend latency and long-running data queries.",
  },
};

/**
 * Resolve an operator-facing message for an error code.
 * Returns English by default, or Chinese when lang='zh'.
 * Unknown codes fall back to `DEFAULT_ENTRY` + the raw code for context.
 */
export function resolveAdminErrorMessage(
  code: string,
  lang: AdminLocale = "en",
): string {
  const entry = (ADMIN_ERROR_MESSAGES as Record<string, AdminErrorEntry>)[code];
  const resolved = entry ?? getHttpFallbackEntry(code);
  if (!resolved) return `${DEFAULT_ENTRY[lang]} (${code})`;
  return resolved[lang];
}

/** Return the full entry (operator message + description) for tooling. */
export function getAdminErrorEntry(code: string): AdminErrorEntry {
  const entry =
    (ADMIN_ERROR_MESSAGES as Record<string, AdminErrorEntry>)[code] ??
    getHttpFallbackEntry(code);
  if (entry) return entry;
  return {
    ...DEFAULT_ENTRY,
    description: `${DEFAULT_ENTRY.description} (code=${code})`,
  };
}

function getHttpFallbackEntry(code: string): AdminErrorEntry | null {
  const known = HTTP_FALLBACK_ERROR_MESSAGES[code];
  if (known) return known;
  const status = code.match(/^HTTP_(\d{3})$/u)?.[1];
  if (!status) return null;
  return {
    en: `Admin API returned HTTP ${status}`,
    zh: `后台接口返回 HTTP ${status}`,
    description: `API returned HTTP ${status} without a structured error body. Inspect the Network panel response and backend logs.`,
  };
}

export type { AdminLocale, AdminErrorEntry };
export { ERROR_CODES };
